import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { ConfirmationDialog, Message } from 'components/Ui-V3/Dialog';
import { useMemo } from 'react';
import { useCurrentInquiry } from 'stores/current-inquiry';
import { useUserContext } from 'stores/user';
import { useVenueInquiryModals, venueInquiryModalsActions } from 'stores/venue-inquiry';
import { ExternalLink } from 'ui';

/**
 * Single component containing all venue inquiry modals.
 * Place this at the parent level of sibling routes that need these modals.
 */
export const VenueInquiryModals = () => {
    const { userTier } = useUserContext();
    const { venues: currentInquiryVenues } = useCurrentInquiry();
    const { stagedVenue, selectedVenueInquiry, stagedAction, selectedVenueInquiryAction, openConfirmationDialog } =
        useVenueInquiryModals();
    const { updatingVenues } = useCurrentInquiry();

    const isInInquiry = useMemo(() => {
        if (!stagedVenue || !currentInquiryVenues) return false;
        const inquiryVenue = currentInquiryVenues.find(v => v.id === stagedVenue.id);
        return !!inquiryVenue;
    }, [currentInquiryVenues, stagedVenue]);

    return (
        <>
            {/* Staged Venue Confirmation Modal */}
            {stagedVenue && (
                <ConfirmationDialog
                    open
                    onConfirm={stagedAction}
                    onCancel={() => venueInquiryModalsActions.setStagedVenue(undefined)}
                    title={isInInquiry ? 'Re-submit Inquiry' : 'Add to inquiry'}
                    message={
                        stagedVenue.tempClosed ? (
                            <Message>
                                This venue cannot be added because it is temporarily closed. If you think this is a
                                mistake, please{' '}
                                <ExternalLink
                                    onClick={() => {
                                        venueInquiryModalsActions.setStagedVenue(undefined);
                                        showIntercom();
                                    }}
                                >
                                    contact our support team
                                </ExternalLink>
                                .
                            </Message>
                        ) : isInInquiry ? (
                            <Message>
                                You previously submitted an inquiry to this venue and later canceled it. Would you like
                                to submit it again?
                            </Message>
                        ) : (
                            <Message>
                                Adding a venue will automatically submit an inquiry to the venue. Do you want to add
                                this venue to your inquiries?
                            </Message>
                        )
                    }
                    actionButtonText={isInInquiry ? 'Re-submit' : 'Add'}
                    actionButtonLoading={updatingVenues}
                    isActionButtonDisabled={stagedVenue.tempClosed}
                    hideIcon
                />
            )}

            {/* Enterprise Non-Preferred Venue Modal */}
            {selectedVenueInquiry && userTier() === 'enterprise' && (
                <ConfirmationDialog
                    open={openConfirmationDialog}
                    onConfirm={selectedVenueInquiryAction}
                    onCancel={() => venueInquiryModalsActions.setOpenConfirmationDialog(false)}
                    title="Confirmation"
                    message="You are about to add a non-preferred venue to your inquiry. It is always suggested that you reach out to preferred venues."
                    cancelButtonText="Keep Searching"
                    actionButtonText="Add Venue to Shortlist"
                    actionButtonLoading={updatingVenues}
                    isActionButtonDisabled={updatingVenues}
                    hideIcon
                />
            )}
        </>
    );
};
